import {
  S3Client,
  GetObjectCommand,
  PutObjectCommand,
  DeleteObjectCommand,
} from '@aws-sdk/client-s3';
import { Injectable } from '@angular/core';
import { fetchAuthSession } from 'aws-amplify/auth';
import { environment } from 'src/environments/environment';

const REGION = 'eu-west-3';
const BUCKET = environment.bucket;

async function bodyToText(body: any): Promise<string> {
  if (typeof body?.transformToString === 'function') {
    return body.transformToString();
  }

  const uint8 = await bodyToUint8Array(body);
  return new TextDecoder('utf-8').decode(uint8);
}

async function bodyToUint8Array(body: any): Promise<Uint8Array> {
  if (typeof body?.transformToByteArray === 'function') {
    const bytes = await body.transformToByteArray();
    return new Uint8Array(bytes);
  }

  if (body instanceof ReadableStream) {
    const reader = body.getReader();
    const chunks: Uint8Array[] = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) chunks.push(value);
    }

    const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
    const merged = new Uint8Array(totalLength);

    let offset = 0;
    for (const chunk of chunks) {
      merged.set(chunk, offset);
      offset += chunk.length;
    }

    return merged;
  }

  throw new Error('Unsupported body type');
}

@Injectable({
  providedIn: 'root',
})
export class S3Service {
  private async createClient(): Promise<S3Client> {
    const session = await fetchAuthSession({ forceRefresh: true });

    if (!session.credentials) {
      throw new Error('Aucun credential AWS disponible dans la session Amplify');
    }

    return new S3Client({
      region: REGION,
      credentials: {
        accessKeyId: session.credentials.accessKeyId,
        secretAccessKey: session.credentials.secretAccessKey,
        sessionToken: session.credentials.sessionToken,
      },
    });
  }

  async getTextObject(key: string): Promise<string> {
    const client = await this.createClient();

    const response = await client.send(
      new GetObjectCommand({
        Bucket: BUCKET,
        Key: key,
      })
    );

    if (!response.Body) {
      throw new Error(`Objet vide pour la clé ${key}`);
    }

    return bodyToText(response.Body);
  }

  async getObject(key: string): Promise<{
    body: Uint8Array;
    contentType?: string;
    contentLength?: number;
    lastModified?: Date;
    etag?: string;
  }> {
    const client = await this.createClient();

    const response = await client.send(
      new GetObjectCommand({
        Bucket: BUCKET,
        Key: key,
      })
    );

    if (!response.Body) {
      throw new Error(`Objet vide pour la clé ${key}`);
    }

    const body = await bodyToUint8Array(response.Body);

    return {
      body,
      contentType: response.ContentType,
      contentLength: response.ContentLength,
      lastModified: response.LastModified,
      etag: response.ETag,
    };
  }

  async getObjectAsBlob(key: string): Promise<Blob> {
    const result = await this.getObject(key);

    const uint8Array = new Uint8Array(result.body);

    return new Blob([uint8Array], {
      type: result.contentType || 'application/octet-stream',
    });
  }

  async getObjectUrl(key: string): Promise<string> {
    const blob = await this.getObjectAsBlob(key);
    return URL.createObjectURL(blob);
  }

  async putFile(params: {
    key: string;
    body: Blob | File | Uint8Array | string;
    contentType?: string;
  }): Promise<boolean> {
    const client = await this.createClient();

    await client.send(
      new PutObjectCommand({
        Bucket: BUCKET,
        Key: params.key,
        Body: params.body,
        ContentType: params.contentType,
      })
    );

    return true;
  }

  async deleteObject(key: string): Promise<boolean> {
    const client = await this.createClient();

    await client.send(
      new DeleteObjectCommand({
        Bucket: BUCKET,
        Key: key,
      })
    );

    return true;
  }
}